
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import type { DonationCampaign, AdoptionCase, Shelter, User } from '../../utils/supabase';
import ContactFooter from '../../components/feature/ContactFooter';

export default function Home() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('donar');
  const [donationCases, setDonationCases] = useState<DonationCampaign[]>([]);
  const [adoptionCases, setAdoptionCases] = useState<AdoptionCase[]>([]);
  const [shelters, setShelters] = useState<Shelter[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Estados para manejar imágenes de cada card individualmente
  const [currentImageIndices, setCurrentImageIndices] = useState<Record<string, number>>({});

  // Función para cambiar imagen de un card específico
  const changeImage = (itemId: string, direction: 'next' | 'prev', totalImages: number) => {
    setCurrentImageIndices(prev => {
      const currentIndex = prev[itemId] || 0;
      let newIndex;

      if (direction === 'next') {
        newIndex = currentIndex < totalImages - 1 ? currentIndex + 1 : 0;
      } else {
        newIndex = currentIndex > 0 ? currentIndex - 1 : totalImages - 1;
      }

      return {
        ...prev,
        [itemId]: newIndex
      };
    });
  };

  // Función para obtener la imagen actual de un item
  const getCurrentImage = (itemId: string, images: string[]) => {
    const currentIndex = currentImageIndices[itemId] || 0;
    return images[currentIndex] || images[0];
  };

  // Función para verificar si hay sesión válida
  const checkSessionValidity = () => {
    const sessionData = localStorage.getItem('user_session');
    if (!sessionData) return false;

    try {
      const session = JSON.parse(sessionData);
      const now = Date.now();

      // Verificar si la sesión ha expirado (3 horas)
      if (now > session.expiresAt) {
        localStorage.removeItem('user_session');
        return false;
      }

      return true;
    } catch (error) {
      localStorage.removeItem('user_session');
      return false;
    }
  };

  // Verificar sesión al cargar
  useEffect(() => {
    setIsLoggedIn(checkSessionValidity());
  }, []);

  // Cargar datos desde Supabase
  useEffect(() => {
    loadPublicData();
  }, []);

  const loadPublicData = async () => {
    try {
      setLoading(true);

      // Cargar campañas activas desde la base de datos
      const { data: campaigns, error: campaignsError } = await supabase
        .from('donation_campaigns')
        .select('*')
        .eq('active', true)
        .order('urgent', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(6);

      if (campaignsError) {
        console.error('Error cargando campañas:', campaignsError);
      }

      // Cargar casos de adopción disponibles desde la base de datos
      const { data: adoptions, error: adoptionsError } = await supabase
        .from('adoption_cases')
        .select('*')
        .eq('adopted', false)
        .order('created_at', { ascending: false })
        .limit(8);

      if (adoptionsError) {
        console.error('Error cargando adopciones:', adoptionsError);
      }

      // Cargar TODOS los refugios activos desde la base de datos (sin límite)
      const { data: shelterData, error: sheltersError } = await supabase
        .from('shelters')
        .select('*')
        .eq('active', true)
        .order('created_at', { ascending: false });

      if (sheltersError) {
        console.error('Error cargando refugios:', sheltersError);
      }

      // Cargar información de usuarios/refugios para mostrar etiquetas
      const { data: userData, error: usersError } = await supabase
        .from('users')
        .select('*');

      if (usersError) {
        console.error('Error cargando usuarios:', usersError);
      }

      setDonationCases(campaigns || []);
      setAdoptionCases(adoptions || []);
      setShelters(shelterData || []);
      setUsers(userData || []);
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  // Función para formatear números
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('es-MX').format(num);
  };

  // Función para calcular porcentaje
  const calculatePercentage = (raised: number, goal: number) => {
    return Math.min((raised / goal) * 100, 100);
  };

  const handleAdminClick = () => {
    if (isLoggedIn) {
      window.REACT_APP_NAVIGATE('/admin');
    } else {
      window.REACT_APP_NAVIGATE('/login');
    }
  };

  const handleMenuClick = (tab: string) => {
    setActiveTab(tab);
    setIsMobileMenuOpen(false);

    // Desplazamiento suave hacia la sección de contenido con más offset y mejor timing
    setTimeout(() => {
      const mainContent = document.querySelector('main');
      if (mainContent) {
        const headerHeight = 80;
        const extraOffset = 60; // Aumentado para mejor visualización
        const offsetTop = mainContent.offsetTop - headerHeight - extraOffset;
        window.scrollTo({
          top: offsetTop,
          behavior: 'smooth'
        });
      }
    }, 200); // Aumentado el delay para mejor fluidez
  };

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);

    // Desplazamiento suave hacia la sección de contenido con más offset y mejor timing
    setTimeout(() => {
      const mainContent = document.querySelector('main');
      if (mainContent) {
        const headerHeight = 80;
        const extraOffset = 60; // Aumentado para mejor visualización
        const offsetTop = mainContent.offsetTop - headerHeight - extraOffset;
        window.scrollTo({
          top: offsetTop,
          behavior: 'smooth'
        });
      }
    }, 200); // Aumentado el delay para mejor fluidez
  };

  const handleNavigationClick = (path: string) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  // Función para obtener el nombre del refugio por user_id
  const getShelterName = (userId: string) => {
    const user = users.find(u => u.id === userId);
    return user?.shelter_name || 'Refugio';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-lg sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-3 mr-32">
              <img
                src="/images/image.png"
                alt="Huellitas Unidas Logo"
                className="w-16 h-16 object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: '"Pacifico", serif' }}>
                  Huellitas Unidas
                </h1>
                <p className="text-sm text-gray-600">Salvando vidas, una patita a la vez</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex gap-2 bg-gray-100 rounded-2xl p-1">
              <button
                onClick={() => handleTabClick('donar')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeTab === 'donar'
                    ? 'bg-orange-500 text-white shadow-lg'
                    : 'text-gray-600 hover:text-orange-500 hover:bg-white'
                }`}
              >
                <i className="ri-heart-fill"></i>
                <span>Donar</span>
              </button>
              <button
                onClick={() => handleTabClick('adoptar')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeTab === 'adoptar'
                    ? 'bg-blue-500 text-white shadow-lg'
                    : 'text-gray-600 hover:text-blue-500 hover:bg-white'
                }`}
              >
                <i className="ri-home-heart-fill"></i>
                <span>Adoptar</span>
              </button>
              <button
                onClick={() => handleTabClick('refugios')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeTab === 'refugios'
                    ? 'bg-green-500 text-white shadow-lg'
                    : 'text-gray-600 hover:text-green-500 hover:bg-white'
                }`}
              >
                <i className="ri-building-fill"></i>
                <span>Refugios</span>
              </button>
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/mision-huellitas')}
                className="flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-300 text-gray-600 hover:text-purple-500 hover:bg-white whitespace-nowrap cursor-pointer"
              >
                <i className="ri-rocket-fill"></i>
                <span>Misión Huellitas</span>
              </button>
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/tienda')}
                className="flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-300 text-gray-600 hover:text-orange-500 hover:bg-white whitespace-nowrap cursor-pointer"
              >
                <i className="ri-store-fill"></i>
                <span>Tienda</span>
              </button>
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/huellitas-perdidas')}
                className="flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-300 text-gray-600 hover:text-red-5 hover:bg-white whitespace-nowrap cursor-pointer"
              >
                <i className="ri-search-eye-fill"></i>
                <span>Huellitas Perdidas</span>
              </button>
              <button
                onClick={() => handleNavigationClick('/pricing')}
                className="flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-300 text-gray-600 hover:text-purple-5 hover:bg-white whitespace-nowrap cursor-pointer"
              >
                <i className="ri-user-add-fill"></i>
                <span>Registrarse</span>
              </button>
              <button
                onClick={handleAdminClick}
                className="flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-300 text-gray-600 hover:text-gray-800 hover:bg-white whitespace-nowrap cursor-pointer"
              >
                <span>Iniciar sesión</span>
              </button>
            </nav>

            {/* Mobile Menu Button */}
            <div className="lg:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-lg text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
              </button>
            </div>
          </div>

          {/* Mobile Menu Dropdown */}
          {isMobileMenuOpen && (
            <div className="lg:hidden absolute left-0 right-0 top-full bg-white border-t shadow-lg z-40">
              <div className="px-4 py-2 space-y-1">
                <button
                  onClick={() => handleMenuClick('donar')}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all duration-300 cursor-pointer ${
                    activeTab === 'donar' ? 'bg-orange-500 text-white shadow-lg' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <i className="ri-heart-fill"></i>
                  <span>Donar</span>
                </button>
                <button
                  onClick={() => handleMenuClick('adoptar')}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 cursor-pointer ${
                    activeTab === 'adoptar' ? 'bg-blue-500 text-white shadow-lg' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <i className="ri-home-heart-fill"></i>
                  <span>Adoptar</span>
                </button>
                <button
                  onClick={() => handleMenuClick('refugios')}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 cursor-pointer ${
                    activeTab === 'refugios' ? 'bg-green-500 text-white shadow-lg' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <i className="ri-building-fill"></i>
                  <span>Refugios</span>
                </button>
                <button
                  onClick={() => {
                    window.REACT_APP_NAVIGATE('/mision-huellitas');
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 text-gray-600 hover:bg-gray-50 cursor-pointer"
                >
                  <i className="ri-rocket-fill"></i>
                  <span>Misión Huellitas</span>
                </button>
                <button
                  onClick={() => {
                    window.REACT_APP_NAVIGATE('/tienda');
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 text-gray-600 hover:bg-gray-50 cursor-pointer"
                >
                  <i className="ri-store-fill"></i>
                  <span>Tienda</span>
                </button>
                <button
                  onClick={() => {
                    window.REACT_APP_NAVIGATE('/huellitas-perdidas');
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 text-gray-600 hover:bg-gray-50 cursor-pointer"
                >
                  <i className="ri-search-eye-fill"></i>
                  <span>Huellitas Perdidas</span>
                </button>
                <div className="border-t pt-2 mt-2">
                  <button
                    onClick={() => handleNavigationClick('/pricing')}
                    className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 text-gray-600 hover:bg-gray-50 cursor-pointer"
                  >
                    <i className="ri-user-add-fill"></i>
                    <span>Registrarse</span>
                  </button>
                  <button
                    onClick={() => {
                      handleAdminClick();
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 text-gray-600 hover:bg-gray-50 cursor-pointer"
                  >
                    <span>Iniciar sesión</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section
        className="relative text-white py-20 overflow-hidden"
        style={{
          backgroundImage: `url('https://static.readdy.ai/image/2d8922a617b4283fedb889b9400131db/5f639615850b1703157b0c2a9ec2a00e.jfif')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="relative container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="text-center lg:text-left">
              <h2 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Cada Perrito<br />
                <span className="text-yellow-300">Merece Amor</span>
              </h2>
              <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto lg:mx-0 opacity-90">
                Únete a nuestra misión de rescatar, cuidar y encontrar hogares amorosos para perritos necesitados
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button
                  onClick={() => handleTabClick('donar')}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-heart-fill mr-2"></i>
                  Hacer Donación
                </button>
                <button
                  onClick={() => handleTabClick('adoptar')}
                  className="bg-white/20 hover:bg-white/30 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 backdrop-blur-sm whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-home-heart-fill mr-2"></i>
                  Ver Adopciones
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <i className="ri-loader-4-line text-4xl text-orange-500 animate-spin mb-4"></i>
              <p className="text-gray-600">Cargando información...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Donation Cases */}
            {activeTab === 'donar' && (
              <section className="space-y-8">
                <div className="text-center">
                  <h2 className="text-4xl font-bold text-gray-800 mb-4">Campañas de Donación Activas</h2>
                  <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                    Cada donación, sin importar el monto, hace la diferencia en la vida de un perrito necesitado
                  </p>
                </div>

                {donationCases.length === 0 ? (
                  <div className="text-center py-12">
                    <i className="ri-heart-line text-6xl text-gray-300 mb-4"></i>
                    <p className="text-gray-500 text-lg">No hay campañas activas en este momento</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-product-shop>
                    {donationCases.map((case_item) => {
                      const images = [
                        case_item.image_url,
                        case_item.image_url_2,
                        case_item.image_url_3,
                        case_item.image_url_4
                      ].filter((url): url is string => Boolean(url));

                      const hasMultipleImages = images.length > 1;
                      const currentImage = getCurrentImage(case_item.id, images);
                      const currentIndex = currentImageIndices[case_item.id] || 0;

                      return (
                        <div
                          key={case_item.id}
                          className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer"
                          onClick={() => window.REACT_APP_NAVIGATE(`/donation/${case_item.id}`)}
                        >
                          {case_item.urgent && (
                            <div className="bg-red-500 text-white px-4 py-2 text-sm font-semibold">
                              <i className="ri-alarm-warning-fill mr-1"></i>
                              URGENTE
                            </div>
                          )}

                          {/* Imagen con navegación */}
                          <div className="relative">
                            <img
                              src={currentImage || 'https://readdy.ai/api/search-image?query=Cute%20rescue%20dog%20needing%20medical%20help%2C%20veterinary%20care%2C%20compassionate%20animal%20rescue%2C%20professional%20pet%20photography%2C%20heartwarming%20scene&width=400&height=300&seq=default-donation&orientation=landscape'}
                              alt={case_item.title}
                              // className="w-full h-48 object-contain object-top"
                              className="w-full h-[45vh] object-cover object-top"
                            />

                            {/* Contador de imágenes */}
                            {hasMultipleImages && (
                              <div className="absolute top-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded-full text-xs">
                                <i className="ri-image-line mr-1"></i>
                                {currentIndex + 1}/{images.length}
                              </div>
                            )}

                            {/* Botones de navegación */}
                            {hasMultipleImages && (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    changeImage(case_item.id, 'prev', images.length);
                                  }}
                                  className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-1 rounded-full transition-all duration-200"
                                >
                                  <i className="ri-arrow-left-line text-sm"></i>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    changeImage(case_item.id, 'next', images.length);
                                  }}
                                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-1 rounded-full transition-all duration-200"
                                >
                                  <i className="ri-arrow-right-line text-sm"></i>
                                </button>
                              </>
                            )}
                          </div>

                          <div className="p-6">
                            <h3 className="text-xl font-bold text-gray-800 mb-2">{case_item.title}</h3>

                            {/* Etiqueta del refugio */}
                            <div className="mb-3">
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                <i className="ri-building-fill mr-1"></i>
                                {getShelterName(case_item.user_id)}
                              </span>
                            </div>

                            <p className="text-gray-600 mb-4 text-sm leading-relaxed">{case_item.description}</p>

                            <div className="mb-4">
                              <div className="flex justify-between text-sm text-gray-600 mb-2">
                                <span>Recaudado: ${formatNumber(case_item.raised || 0)}</span>
                                <span>Meta: ${formatNumber(case_item.goal || 0)}</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-3">
                                <div
                                  className="bg-gradient-to-r from-orange-400 to-orange-600 h-3 rounded-full transition-all duration-500"
                                  style={{ width: `${calculatePercentage(case_item.raised || 0, case_item.goal || 1)}%` }}
                                ></div>
                              </div>
                            </div>

                            <div className="flex justify-between items-center text-sm text-gray-600 mb-4">
                              <span className="text-orange-600 font-semibold">
                                {Math.round(calculatePercentage(case_item.raised || 0, case_item.goal || 1))}% completado
                              </span>
                            </div>

                            <button
                              className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.REACT_APP_NAVIGATE(`/donation/${case_item.id}`);
                              }}
                            >
                              <i className="ri-heart-fill mr-2"></i>
                              Ver Detalles
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            )}

            {/* Adoption Cases */}
            {activeTab === 'adoptar' && (
              <section className="space-y-8">
                <div className="text-center">
                  <h2 className="text-4xl font-bold text-gray-800 mb-4">Perritos en Adopción</h2>
                  <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                    Estos hermosos perritos están buscando un hogar lleno de amor
                  </p>
                </div>

                {adoptionCases.length === 0 ? (
                  <div className="text-center py-12">
                    <i className="ri-home-heart-line text-6xl text-gray-300 mb-4"></i>
                    <p className="text-gray-500 text-lg">No hay perritos disponibles para adopción en este momento</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {adoptionCases.map((dog) => {
                      const images = [
                        dog.image_url,
                        dog.image_url_2,
                        dog.image_url_3,
                        dog.image_url_4
                      ].filter((url): url is string => Boolean(url));

                      const hasMultipleImages = images.length > 1;
                      const currentImage = getCurrentImage(dog.id, images);
                      const currentIndex = currentImageIndices[dog.id] || 0;

                      return (
                        <div
                          key={dog.id}
                          className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer"
                          onClick={() => window.REACT_APP_NAVIGATE(`/adoption/${dog.id}`)}
                        >
                          {/* Imagen con navegación */}
                          <div className="relative">
                            <img
                              src={currentImage || 'https://readdy.ai/api/search-image?query=Adorable%20rescue%20dog%20ready%20for%20adoption%2C%20happy%20healthy%20pet%20looking%20for%20loving%20home%2C%20professional%20animal%20photography%2C%20heartwarming%20adoption%20portrait&width=400&height=300&seq=default-adoption&orientation=landscape'}
                              alt={dog.name}
                              className="w-full h-64 object-cover object-top"
                            />

                            {/* Contador de imágenes */}
                            {hasMultipleImages && (
                              <div className="absolute top-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded-full text-xs">
                                <i className="ri-image-line mr-1"></i>
                                {currentIndex + 1}/{images.length}
                              </div>
                            )}

                            {/* Botones de navegación */}
                            {hasMultipleImages && (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    changeImage(dog.id, 'prev', images.length);
                                  }}
                                  className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-1 rounded-full transition-all duration-200"
                                >
                                  <i className="ri-arrow-left-line text-sm"></i>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    changeImage(dog.id, 'next', images.length);
                                  }}
                                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-1 rounded-full transition-all duration-200"
                                >
                                  <i className="ri-arrow-right-line text-sm"></i>
                                </button>
                              </>
                            )}
                          </div>

                          <div className="p-6">
                            <div className="flex justify-between items-start mb-3">
                              <h3 className="text-2xl font-bold text-gray-800">{dog.name}</h3>
                              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                                {dog.age}
                              </span>
                            </div>

                            {/* Etiqueta del refugio */}
                            <div className="mb-3">
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                <i className="ri-building-fill mr-1"></i>
                                {getShelterName(dog.user_id)}
                              </span>
                            </div>

                            <p className="text-gray-600 font-medium mb-2">{dog.breed}</p>
                            <p className="text-gray-600 mb-4 text-sm leading-relaxed">{dog.description}</p>

                            <div className="flex items-center text-sm text-gray-600 mb-4">
                              <i className="ri-map-pin-fill mr-1 text-red-500"></i>
                              <span>{dog.location}</span>
                            </div>

                            <div className="flex gap-2 mb-4">
                              {dog.vaccinated && (
                                <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-semibold">
                                  <i className="ri-shield-check-fill mr-1"></i>Vacunado
                                </span>
                              )}
                              {dog.sterilized && (
                                <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-semibold">
                                  <i className="ri-scissors-fill mr-1"></i>Esterilizado
                                </span>
                              )}
                            </div>

                            <button
                              className="w-full bg-blue-500 hover:bg-blue-600 text-white py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.REACT_APP_NAVIGATE(`/adoption/${dog.id}`);
                              }}
                            >
                              <i className="ri-home-heart-fill mr-2"></i>
                              Ver Detalles
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            )}

            {/* Shelters */}
            {activeTab === 'refugios' && (
              <section className="space-y-8">
                <div className="text-center">
                  <h2 className="text-4xl font-bold text-gray-800 mb-4">Refugios Certificados</h2>
                  <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                    Conoce los refugios que trabajan incansablemente por el bienestar animal
                  </p>
                </div>

                {shelters.length === 0 ? (
                  <div className="text-center py-12">
                    <i className="ri-building-line text-6xl text-gray-300 mb-4"></i>
                    <p className="text-gray-500 text-lg">No hay refugios registrados en este momento</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg-grid-cols-3 gap-8">
                    {shelters.map((shelter) => {
                      const images = [
                        shelter.image_url,
                        shelter.image_url_2,
                        shelter.image_url_3,
                        shelter.image_url_4
                      ].filter((url): url is string => Boolean(url));

                      const hasMultipleImages = images.length > 1;
                      const currentImage = getCurrentImage(shelter.id, images);
                      const currentIndex = currentImageIndices[shelter.id] || 0;

                      return (
                        <div key={shelter.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300">
                          {/* Imagen con navegación */}
                          <div className="relative">
                            <img
                              src={currentImage || 'https://readdy.ai/api/search-image?query=Professional%20animal%20shelter%20facility%2C%20clean%20modern%20dog%20rescue%20center%2C%20caring%20environment%20for%20rescued%20pets%2C%20veterinary%20care%20facility&width=600&height=400&seq=default-shelter&orientation=landscape'}
                              alt={shelter.name}
                              className="w-full h-80 object-cover object-top"
                            />

                            {/* Contador de imágenes */}
                            {hasMultipleImages && (
                              <div className="absolute top-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded-full text-xs">
                                <i className="ri-image-line mr-1"></i>
                                {currentIndex + 1}/{images.length}
                              </div>
                            )}

                            {/* Botones de navegación */}
                            {hasMultipleImages && (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    changeImage(shelter.id, 'prev', images.length);
                                  }}
                                  className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-1 rounded-full transition-all duration-200"
                                >
                                  <i className="ri-arrow-left-line text-sm"></i>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    changeImage(shelter.id, 'next', images.length);
                                  }}
                                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-1 rounded-full transition-all duration-200"
                                >
                                  <i className="ri-arrow-right-line text-sm"></i>
                                </button>
                              </>
                            )}
                          </div>

                          <div className="p-6">
                            <h3 className="text-xl font-bold text-gray-800 mb-2">{shelter.name}</h3>
                            <div className="flex items-center text-gray-600 mb-3">
                              <i className="ri-map-pin-fill mr-2 text-red-500"></i>
                              <span>{shelter.location}</span>
                            </div>

                            <p className="text-gray-600 mb-4 text-sm leading-relaxed">{shelter.description}</p>

                            <div className="flex justify-between items-center text-sm text-gray-600 mb-4">
                              <span>Capacidad:</span>
                              <span className="font-medium">{shelter.current_dogs}/{shelter.capacity} perritos</span>
                            </div>

                            <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                              <div
                                className="bg-green-500 h-2 rounded-full transition-all duration-500"
                                style={{ width: `${Math.min(100, (shelter.current_dogs / shelter.capacity) * 100)}%` }}
                              ></div>
                            </div>

                            <div className="flex flex-wrap gap-2 mb-4">
                              <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-semibold">
                                <i className="ri-shield-check-fill mr-1"></i>Certificado
                              </span>
                              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-semibold">
                                <i className="ri-heart-fill mr-1"></i>Activo
                              </span>
                            </div>

                            <button
                              className="w-full bg-green-500 hover:bg-green-600 text-white py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
                              onClick={() => window.REACT_APP_NAVIGATE(`/shelter/${shelter.id}`)}
                            >
                              <i className="ri-building-fill mr-2"></i>
                              Ver Refugio
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            )}
          </>
        )}
      </main>

      {/* Contact Footer */}
      <ContactFooter />
    </div>
  );
}
